/**
 * §18.7 — one run's earnings statement: the pay stub Texas Labor Code §62.003
 * asks for at the end of each pay period (payroll stage 4).
 *
 * THE SERVER BUILDS IT (apps/api/src/payroll/earnings-statement.ts) and refuses
 * one that does not add up; this lays it out and prints it, and computes
 * nothing.
 *
 * PRINTED, IT IS THE STATEMENT AND NOTHING ELSE. §62.003(b) asks for it signed
 * by the employer or the employer's agent, so it carries a signature line; and
 * styles.css's print rules drop the navigation, the banners and this screen's
 * own controls (`no-print`), leaving the page an employee is handed.
 *
 * A WITHDRAWN RUN'S STATEMENT SAYS SO, first, on screen and on paper.
 *
 * EVERY STATEMENT SAYS HOW ITS PAY PERIOD WAS TAKEN. A run stores its pay date
 * and frequency, not its period, so the server infers the period whatever the
 * frequency, and a monthly run paid in arrears would otherwise name the wrong
 * month on a signed page without a word (the round-3 review, R3-1).
 *
 * NO SOCIAL SECURITY NUMBER: §62.003 asks for none, and none is sent.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { InfoNote } from '../components/InfoNote.js';
import { ApiError, api, type EarningsStatement } from '../api.js';
import { formatCents } from '../lib/money.js';

const PER: Record<EarningsStatement['rate']['frequency'], string> = {
  weekly: 'a week',
  biweekly: 'every two weeks',
  monthly: 'a month',
  quarterly: 'a quarter',
};

/** How apps/api/src/payroll/pay-periods.ts takes each frequency's period. */
const PERIOD_TAKEN_AS: Record<EarningsStatement['rate']['frequency'], string> = {
  weekly: 'the seven days ending on the pay date',
  biweekly: 'the fourteen days ending on the pay date',
  monthly: 'the calendar month containing the pay date',
  quarterly: 'the calendar quarter containing the pay date',
};

export function EarningsStatementPage() {
  const { id } = useParams();
  const [statement, setStatement] = useState<EarningsStatement | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.getEarningsStatement(id!).then(
      (result) => {
        if (live) setStatement(result.statement);
      },
      (caught: unknown) => {
        if (live) setError(caught instanceof ApiError ? caught.message : 'Could not build the statement.');
      },
    );
    return () => {
      live = false;
    };
  }, [id]);

  return (
    <section className="earnings-statement">
      <p className="no-print">
        <Link to={`/payroll/runs/${id}`}>Back to the Run</Link>
      </p>
      <div className="row statement-head no-print">
        <h1>
          Earnings Statement{' '}
          <InfoNote label="What Texas Asks For">
            Texas Labor Code §62.003 asks for a statement at the end of each pay period, signed by the employer or the
            employer&apos;s agent, showing the employee&apos;s name, the rate of pay, the pay earned, each deduction and its
            purpose, and the pay after deductions. It does not apply to anyone the federal Fair Labor Standards Act covers
            (§62.151); whether that includes this business is a question for your CPA. Print it, sign it, and give it to
            the employee.
          </InfoNote>
        </h1>
        {statement && (
          <button type="button" onClick={() => window.print()}>
            Print Statement
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {statement === null && error === null && <p className="muted">Loading…</p>}
      {statement && <Statement statement={statement} />}
    </section>
  );
}

function Statement({ statement: s }: { statement: EarningsStatement }) {
  return (
    <article className="statement" aria-label="Earnings Statement">
      {s.withdrawn && (
        <p className="notice notice-warn">
          This run was {s.withdrawn.status}
          {s.withdrawn.on && (
            <>
              {' '}
              on <span className="date-cell">{s.withdrawn.on}</span>
            </>
          )}
          {s.withdrawn.reason && <>: {s.withdrawn.reason}</>}. This statement is withdrawn.
        </p>
      )}

      <header className="statement-issuer">
        <h2>{s.employer}</h2>
        <p className="muted">Earnings Statement</p>
      </header>

      <dl className="statement-facts">
        <div>
          <dt>Employee</dt>
          <dd>{s.employee}</dd>
        </div>
        <div>
          <dt>Pay Date</dt>
          <dd className="date-cell">{s.payDate}</dd>
        </div>
        <div>
          <dt>Pay Period</dt>
          <dd>
            <span className="date-cell">{s.payPeriod.start}</span> to <span className="date-cell">{s.payPeriod.end}</span>
          </dd>
        </div>
        <div>
          <dt>Rate of Pay</dt>
          <dd>
            {formatCents(s.rate.amountPerPeriodCents)} {PER[s.rate.frequency]}
          </dd>
        </div>
      </dl>
      <p className="muted">The pay period is taken as {PERIOD_TAKEN_AS[s.rate.frequency]}.</p>

      <table className="lines" aria-label="Pay Earned">
        <thead>
          <tr>
            <th>Pay Earned</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {s.earnings.map((line) => (
            <tr key={line.label}>
              <td>{line.label}</td>
              <td className="num">{formatCents(line.cents)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total Pay Earned</th>
            <td className="num">{formatCents(s.totalEarnedCents)}</td>
          </tr>
        </tfoot>
      </table>

      <table className="lines" aria-label="Deductions">
        <thead>
          <tr>
            <th>Deduction</th>
            <th>Purpose</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {s.deductions.length === 0 ? (
            <tr>
              <td colSpan={3} className="muted">
                Nothing was deducted.
              </td>
            </tr>
          ) : (
            s.deductions.map((line) => (
              <tr key={line.label}>
                <td>{line.label}</td>
                <td>{line.purpose}</td>
                <td className="num">{formatCents(line.cents)}</td>
              </tr>
            ))
          )}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" colSpan={2}>
              Total Deductions
            </th>
            <td className="num">{formatCents(s.totalDeductionsCents)}</td>
          </tr>
        </tfoot>
      </table>

      <p className="statement-net">
        <span>Net Pay</span> <span className="num">{formatCents(s.netPayCents)}</span>
      </p>

      {s.reportedNotPaid.length > 0 && (
        <div className="statement-reported">
          <h3>Reported as Wages, Not Paid to You</h3>
          <ul className="history-list">
            {s.reportedNotPaid.map((line) => (
              <li key={line.label}>
                {line.label} · <span className="num">{formatCents(line.cents)}</span>
              </li>
            ))}
          </ul>
          <p className="muted">
            Paid by the business for your health insurance. It is on your W-2 as wages and taxed for income tax; it was
            not paid to you, and it is not in the pay above.
          </p>
        </div>
      )}

      <p className="statement-signature">
        Signed for {s.employer}: <span className="signature-line" aria-hidden="true" />
      </p>
    </article>
  );
}
